import { Injectable } from "@angular/core";
import { IUser } from "@app/interfaces/IUser";
import { CurrentUserService } from "@app/services/currentUser.service";
import { DateService } from "@app/services/date.service";
import { TranslationService } from "@app/services/translation.service";
import { UsersService } from "@app/services/users.service";
import { Subject } from "rxjs";
import { VideoCoachingStatus } from "../enums/VideoCoachingStatus.enum";
import { VideoReviewStatus } from "../enums/VideoReviewStatus.enum";
import { VideoReviewSubStatus } from "../enums/VideoReviewSubStatus.enum";
import { IUserAction } from "../models/IUserAction";
import { IVideoEntry } from "../models/IVideoEntry";
import { IVideoMetaData } from "../models/IVideoMetaData";
import { IVideoTimelineEvent } from "../models/IVideoTimelineEvent";
import { VideoEntryService } from "./video-entry.service";
import { VideoReviewStatusService } from "./video-review-status.service";

@Injectable({
    providedIn: 'root'
})
export class VideoReviewService {
    private unseenVideoCountUpdatedSubject = new Subject<void>();
    private favouriteVideoCountUpdatedSubject = new Subject<void>();

    unseenVideoCountUpdated$ = this.unseenVideoCountUpdatedSubject.asObservable();
    favouriteVideoCountUpdated$ = this.favouriteVideoCountUpdatedSubject.asObservable();

    constructor(
        private readonly currentUserService: CurrentUserService,
        private readonly translationService: TranslationService,
        private readonly dateService: DateService,
        private readonly videoEntryService: VideoEntryService,
        private readonly usersService: UsersService,
        private readonly videoReviewStatusService: VideoReviewStatusService
    ) { }

    createVideoTimeline(videoEntry: IVideoEntry, users: IUser[]): IVideoTimelineEvent[] {
        const timeline: IVideoTimelineEvent[] = [];

        const checkIfExistAndAddToTimeline = (actions: IUserAction[], callback: (action: IUserAction) => IVideoTimelineEvent): void => {
            if (!actions) {
                return;
            }

            actions.forEach(a => {
                const userAction = callback(a);
                timeline.push(userAction);
            })
        }

        if (videoEntry.EventTimeUtc) {
            const event: IVideoTimelineEvent = {
                ActionDate: videoEntry.EventTimeUtc,
                displayText: this.translationService.t("cameraService.eventTime"),
                icon: "event_note"
            }

            timeline.push(event);
        }

        if (videoEntry.MetaData.UserRequestedUploadDate || videoEntry.DateRequested) {
            const user = users.find(u => u.Id == videoEntry.RequestedBy);
            const comment = this.translationService.t("cameraService.requested");
            const date = videoEntry.MetaData.UserRequestedUploadDate || videoEntry.DateRequested;
            const action = this.getUserAction(user, comment, date);

            timeline.push(action);
        }

        if (videoEntry.DateDownloaded) {
            const comment = this.translationService.t("cameraService.uploadedToCloud");
            const date = videoEntry.DateDownloaded;
            const icon = "cloud_done";
            const action = this.getUserAction(null, comment, date, icon);

            timeline.push(action);
        }

        checkIfExistAndAddToTimeline(videoEntry.MetaData.Watched, (action) => this.findUserAndGetUserAction(action, users, "visibility"));
        checkIfExistAndAddToTimeline(videoEntry.MetaData.Authorised, (action) => this.findUserAndGetUserAction(action, users, "done"));
        checkIfExistAndAddToTimeline(videoEntry.MetaData.Favourite, (action) => this.findUserAndGetUserAction(action, users, "favorite"));

        checkIfExistAndAddToTimeline(videoEntry.MetaData.Notes, (action) => {
            const customDisplayText = this.translationService.t(`cameraService.addedComment`);
            return this.findUserAndGetUserAction(action, users, "visibility", true, customDisplayText);
        });

        timeline.sort((a, b) => this.dateService.sort(a.ActionDate, b.ActionDate));
        return timeline;
    }

    async setVideoReviewed(videoEntry: IVideoEntry, state: boolean): Promise<IVideoEntry> {
        const user = await (this.currentUserService.getCurrentUser() as Promise<IUser>)

        if (!videoEntry.MetaData.Authorised) {
            videoEntry.MetaData.Authorised = [];
        }

        let updateVideo: boolean;

        if (state) {
            const existingAction = this.isActionSetForUser(videoEntry.MetaData.Authorised, user);

            if (!existingAction) {
                const action = this.getUserAction(user, this.translationService.t('cameraService.viewed'));
                videoEntry.MetaData.Authorised.push(action);
            }

            updateVideo = !existingAction;
        } else {
            const actionIndex = videoEntry.MetaData.Authorised.findIndex(action => action.UserId === user.Id);

            if (actionIndex !== -1) {
                videoEntry.MetaData.Authorised.splice(actionIndex, 1);
            }

            updateVideo = actionIndex !== -1;
        }

        if (!updateVideo) {
            return videoEntry;
        }

        videoEntry.reviewed = state;
        
        return this.videoEntryService.updateVideoEntry(videoEntry);
    }

    getReviewStatusIcon(status: VideoReviewStatus): string {
        switch (status) {
            case VideoReviewStatus.Reviewed:
                return "supervisor_account";

            case VideoReviewStatus.Coached:
            case VideoReviewStatus.Reviewed:
                return "playlist_add_check";

            default:
                return "edit_note";
        }
    }

    async markVideoAsWatched(videoEntry: IVideoEntry): Promise<IVideoEntry> {
        if (videoEntry.watched) {
            return videoEntry;
        }

        const user = await (this.currentUserService.getCurrentUser() as Promise<IUser>)

        if (!videoEntry.MetaData.Watched) {
            videoEntry.MetaData.Watched = [];
        }

        const action = this.getUniqueUserAction(videoEntry.MetaData.Watched, user, this.translationService.t('cameraService.viewed'));

        if (!action) {
            return videoEntry;
        }

        videoEntry.MetaData.Watched.push(action);
        videoEntry.watched = true;

        const result = await this.videoEntryService.updateVideoEntry(videoEntry);
        this.unseenVideoCountUpdatedSubject.next();

        return result;
    }

    async setVideoFavourite(videoEntry: IVideoEntry, state: boolean): Promise<IVideoEntry> {
        const user = await (this.currentUserService.getCurrentUser() as Promise<IUser>)

        if (!videoEntry.MetaData.Favourite) {
            videoEntry.MetaData.Favourite = [];
        }

        let updateVideo: boolean;

        if (state) {
            const existingAction = this.isActionSetForUser(videoEntry.MetaData.Favourite, user);

            if (!existingAction) {
                const action = this.getUserAction(user, this.translationService.t('cameraService.favourited'));
                videoEntry.MetaData.Favourite.push(action);
            }

            updateVideo = !existingAction;
        } else {
            const actionIndex = videoEntry.MetaData.Favourite.findIndex(action => action.UserId === user.Id);

            if (actionIndex !== -1) {
                videoEntry.MetaData.Favourite.splice(actionIndex, 1);
            }

            updateVideo = actionIndex !== -1;
        }

        if (!updateVideo) {
            return videoEntry;
        }

        videoEntry.favourite = videoEntry.MetaData.Favourite.some(f => f.UserId === user.Id);
        videoEntry.companyFavourite = videoEntry.MetaData.Favourite.length > 0;

        return await this.videoEntryService.updateVideoEntry(videoEntry);
    }

    async addNoteToVideo(videoEntry: IVideoEntry, noteToAdd: IUserAction): Promise<IVideoEntry> {
        noteToAdd.Status = videoEntry.reviewStatus;
        noteToAdd.CoachingStatus = videoEntry.coachingStatus;
        noteToAdd.ReviewStatus = videoEntry.reviewSubStatus;
        noteToAdd.statusDisplayValue = this.videoReviewStatusService.getVideoReviewStatusDisplayValue(videoEntry);

        if (!videoEntry.MetaData.Notes) {
            videoEntry.MetaData.Watched = [];
        }

        videoEntry.MetaData.Notes.push(noteToAdd);
        return await this.videoEntryService.updateVideoEntry(videoEntry);
    }

    async mapMetaDataNotesToUsers(metaData: IVideoMetaData): Promise<void> {
        const users = await this.usersService.getUsers({includeAdmins: true});

        metaData.Notes.forEach(note => {
            note.user = this.usersService.findUser(users, note.UserId);
        });
    }

    private getUniqueUserAction(userActions: IUserAction[], user: IUser, comment: string): IVideoTimelineEvent {
        const existingAction = this.isActionSetForUser(userActions, user);
        return existingAction ? null : this.getUserAction( user, comment);
    }

    private getUserAction(
        user: IUser,
        comment: string,
        date: Date = null,
        icon: string = null,
        reviewStatus: VideoReviewStatus = VideoReviewStatus.NeedsReview,
        reviewSubStatus: VideoReviewSubStatus = VideoReviewSubStatus.None,
        coachingStatus: VideoCoachingStatus = VideoCoachingStatus.None,
        note = false,
        customDisplayText: string = null
    ): IVideoTimelineEvent {
        date = date || new Date();
        return {
            UserId: user && user.Id,
            ActionDate: date,
            Comment: comment,
            Status: reviewStatus,
            ReviewStatus: reviewSubStatus,
            CoachingStatus: coachingStatus,
            statusDisplayValue: this.videoReviewStatusService.getReviewStatusDisplayValue(reviewStatus, reviewSubStatus, coachingStatus),

            displayText: (customDisplayText || comment),
            icon: icon,
            user: user,
            note: note
        }
    }

    private findUserAndGetUserAction(
        action: IUserAction,
        users: IUser[],
        icon = "",
        note = false,
        customDisplayText: string = null
    ): IVideoTimelineEvent {
        const user = users.find(u => u.Id == action.UserId);
        const date = new Date(action.ActionDate)

        return this.getUserAction(
            user,
            action.Comment,
            date,
            icon,
            action.Status,
            action.ReviewStatus,
            action.CoachingStatus,
            note,
            customDisplayText);
    }


    private isActionSetForUser(userActions: IUserAction[], user: IUser): boolean {
        for (let i = 0; i < userActions.length; i++) {
            const watched = userActions[i];
            if (user.Id === watched.UserId) {
                return true;
            }
        }

        return false;
    }
}
